import html2canvas from 'html2canvas';

/**
 * Captures a screenshot of a DOM element and saves it as a PNG
 * Ensures all background colors, text colors, border colors, and shadows are properly rendered
 * Works in both light and dark themes
 *
 * @param {Object} options - Configuration options for the screenshot
 * @param {React.RefObject} options.contentRef - React ref to the DOM element to capture
 * @param {string} options.filename - The filename for the downloaded PNG (without extension)
 * @param {Function} options.afterCapture - Callback to execute after the screenshot is taken
 * @param {string} options.controlsSelector - CSS selector for elements to hide during capture (default: '.screenshot-controls')
 * @returns {Promise<void>}
 */
export const captureScreenshot = ({
  contentRef,
  filename = 'screenshot',
  afterCapture = () => {},
  controlsSelector = '.screenshot-controls'
}) => {
  if (!contentRef.current) return;

  // Add class to hide controls
  const controls = contentRef.current.querySelectorAll(controlsSelector);
  controls.forEach((control) => control.classList.add('hide-for-screenshot'));

  // Store original styles
  const allElements = Array.from(contentRef.current.querySelectorAll('*'));
  const originalStyles = allElements.map((el) => ({
    el,
    bg: el.style.backgroundColor,
    color: el.style.color,
    borderColor: el.style.borderColor,
    boxShadow: el.style.boxShadow
  }));

  // Force computed styles on every element
  allElements.forEach((el) => {
    const computed = window.getComputedStyle(el);
    // Backgrounds
    const bg = computed.backgroundColor;
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
      el.style.setProperty('background-color', bg, 'important');
    }

    // Text colors
    const color = computed.color;
    if (color) {
      el.style.setProperty('color', color, 'important');
    }

    // Border colors
    const borderColor = computed.borderColor;
    if (borderColor && borderColor !== 'rgba(0, 0, 0, 0)' && borderColor !== 'transparent') {
      el.style.setProperty('border-color', borderColor, 'important');
    }

    // Box shadows (for card effects)
    const boxShadow = computed.boxShadow;
    if (boxShadow && boxShadow !== 'none') {
      el.style.setProperty('box-shadow', boxShadow, 'important');
    }
  });

  return html2canvas(contentRef.current, {
    ignoreElements: (element) => element.classList.contains('hide-for-screenshot'),
    useCORS: true,
    scale: 2 // Higher quality
  }).then((canvas) => {
    const image = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = image;
    link.download = `${filename}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Remove the hide class after screenshot
    controls.forEach((control) => control.classList.remove('hide-for-screenshot'));

    // Restore original styles
    originalStyles.forEach((item) => {
      item.el.style.backgroundColor = item.bg || '';
      item.el.style.color = item.color || '';
      item.el.style.borderColor = item.borderColor || '';
      item.el.style.boxShadow = item.boxShadow || '';
    });

    // Execute callback
    afterCapture();

    return image;
  });
};

export default captureScreenshot;
